import { DecisionTreeNode } from "@amzn/ask-legal-domain";
import { Builder } from "builder-pattern";
import { CustomNode } from "../components/decision-tree-container/custom-resources/CustomNode";
import { CustomEdge } from "../components/decision-tree-container/custom-resources/CustomEdge";
import { MarkerType } from "reactflow";
import { CSSProperties } from "react";
import { TREE_LAYOUT_CONFIG } from "../utils/decisiontree/elk-tree-layout";
import { EdgeClassification } from "../utils/decisiontree/graph-builder";

export const getReactFlowNode = (
    node: DecisionTreeNode,
    location: {x: number, y: number},
    useCustomizedLayout: boolean = false,
    isOrphan: boolean = false,
    isRoot: boolean = false
): CustomNode.Node => {
    const defaultNodeStyle: CSSProperties = {
        border: `${TREE_LAYOUT_CONFIG.NODE_BORDER_WIDTH}px solid black`,
        padding: `${TREE_LAYOUT_CONFIG.NODE_PADDING}px`,
        borderRadius: `${TREE_LAYOUT_CONFIG.NODE_BORDER_RADIUS}px`,
        background: "white",
        minHeight: `${TREE_LAYOUT_CONFIG.NODE_MIN_HEIGHT}px`,
        height: "fit-content",
        width: `${TREE_LAYOUT_CONFIG.NODE_WIDTH}px`,
        fontFamily: "Amazon Ember"
    };

    // Orphan node styling: dashed border, light purple background
    const orphanNodeStyle: CSSProperties = {
        ...defaultNodeStyle,
        border: `${TREE_LAYOUT_CONFIG.NODE_BORDER_WIDTH}px dashed #8B5CF6`,
        background: "#F3F0FF",
    };

    // Determine background color based on node type.
    // Uses explicit isRoot param (rootNodeId comparison from buildGraph) instead of
    // deprecated node.parentNodeId — orphan nodes also lack parentNodeId and would
    // incorrectly turn green when linked to the tree.
    const getNodeBackground = (): string => {
        if (isOrphan) return "#F3F0FF"; // Light purple for orphans
        if (isRoot) return "lightgreen"; // Root node — only the actual tree root
        if (node.children.length === 0) return "#E78C8C"; // Leaf node (red)
        return "white"; // Intermediate node
    };

    return Builder<CustomNode.Node>()
        .id(node.id)
        .type(CustomNode.Type)
        .data({
            overviewText: node.overviewText,
            isLeaf: DecisionTreeNode.isLeafNode(node),
            isRoot: isRoot,
            useCustomizedLayout: useCustomizedLayout,
            isOrphan: isOrphan,
            backgroundColor: getNodeBackground()
        })
        .position(location)
        .style({
            ...(isOrphan ? orphanNodeStyle : defaultNodeStyle),
            background: getNodeBackground()
        })
        .build();
};

export const getReactFlowEdgeId = (from: string, to: string, answer: string = ""): string => {
    // Answer is part of the identity so that multiple distinct answers from the same
    // parent to the same child (convergent edges) are treated as separate edges rather
    // than collapsing into one. Identical answers to the same child intentionally share
    // an id (they collapse to a single edge — see docs/PLAN-converging-edges.md).
    return `${from}::${to}::${answer}`;
};

export const getReactFlowEdge = (
    parentNodeId: string,
    childNodeId: string,
    answer: string,
    childIndex: number = 0,
    totalChildren: number = 1,
    classification?: EdgeClassification
): CustomEdge.Edge => {
    const edge = {
        id: getReactFlowEdgeId(parentNodeId, childNodeId, answer),
        source: parentNodeId,
        target: childNodeId,
        sourceHandle: "b",  // Bottom handle of source node (defined in CustomNode)
        targetHandle: "t",  // Top handle of target node (defined in CustomNode)
        animated: true,
        type: CustomEdge.Type,
        markerEnd: {
            type: MarkerType.Arrow
        },
        data: {
            optionText: answer,
            childIndex,      // Used by CustomEdge to offset label position
            totalChildren,   // Used by CustomEdge to spread labels apart
            classification   // Edge classification for styling (connected/orphan/cross)
        }
    };
    return edge;
};

